import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Lightbulb,
  Filter,
  User,
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Pause,
  MessageSquare,
  ArrowRight,
  Shield,
  TrendingUp,
  Search,
  Download,
  Sparkles,
  ShoppingCart,
  CreditCard,
  Repeat
} from 'lucide-react';

// Channel classification — every insight is classified into ONE of the three
// distribution-channel groups used across the app (E-commerce / POS / Subscriptions),
// each rendered as an icon badge. Untagged/general/online insights default to E-commerce.
const CHANNEL_CLASS = {
  ecommerce: { label: 'E-commerce', Icon: ShoppingCart, iconClass: 'text-blue-600 bg-blue-50 border-blue-200', tooltip: 'Online / e-commerce channel (web, API, mobile, payment links)' },
  pos: { label: 'POS', Icon: CreditCard, iconClass: 'text-slate-700 bg-slate-100 border-slate-200', tooltip: 'Card-present (POS) transaction — no IP/email/cardholder data' },
  subscriptions: { label: 'Subscriptions', Icon: Repeat, iconClass: 'text-indigo-600 bg-indigo-50 border-indigo-200', tooltip: 'Subscription / recurring-billing transaction' },
};

// Map a raw insight channel value to one of the three groups. Returns null when the
// insight has NO channel tag (platform-wide / IP / email / cross-merchant insights) so
// no badge is shown. pos→POS, recurring→Subscriptions, any other real channel
// (web/api/mobile/link/other) → E-commerce.
const classifyChannel = (channel) => {
  const c = (channel || '').toLowerCase().trim();
  if (!c) return null;
  if (c === 'pos') return 'pos';
  if (c === 'recurring') return 'subscriptions';
  return 'ecommerce';
};

const AIInsights = () => {
  const { token, selectedPlatform, backendUrl } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ 
    status: 'new', 
    category: 'all',
    priority: 'all',
    channel: 'all',
    assigned_to: '',
    period: 'thismonth',
    customMonth: new Date().getMonth() + 1,
    customYear: new Date().getFullYear()
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [newComment, setNewComment] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState([]); // base64 data URLs
  const [attachmentError, setAttachmentError] = useState('');
  const [imagePreview, setImagePreview] = useState(null); // URL string when enlarged
  const [saving, setSaving] = useState(false);
  const [cardDesign, setCardDesign] = useState('current');
  const [searchQuery, setSearchQuery] = useState('');
  const [exporting, setExporting] = useState(false);
  const [running, setRunning] = useState(false);
  const [merchantNames, setMerchantNames] = useState([]);

  // Fetch merchant names so they can be linkified inside insight text.
  // We sort by length DESC so longer names match before substrings (e.g. "GadgetPro Solutions" before "GadgetPro").
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const platformParam = selectedPlatform && selectedPlatform !== 'all' ? `&platform_id=${selectedPlatform}` : '';
        const res = await axios.get(`${backendUrl}/api/merchants?per_page=1000${platformParam}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const names = (res.data?.merchants || [])
          .map((m) => m?.name)
          .filter((n) => n && n.length >= 3)
          .sort((a, b) => b.length - a.length);
        setMerchantNames(names);
      } catch (e) {
        console.warn('Failed to load merchant names for linkify', e);
      }
    })();
    return () => { cancelled = true; };
  }, [token, selectedPlatform, backendUrl]);
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [health, setHealth] = useState(null);

  // Fetch lightweight health snapshot on mount + after any agent run.
  const fetchHealth = async () => {
    if (!token) return;
    try {
      const r = await axios.get(
        `${backendUrl}/api/admin/ai-agent/health`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHealth(r.data);
    } catch {
      setHealth(null);
    }
  };

  useEffect(() => {
    fetchHealth();
  }, [token]);

  const handleRunDiagnostics = async () => {
    if (!token || diagLoading) return;
    setDiagLoading(true);
    try {
      const r = await axios.get(
        `${backendUrl}/api/admin/ai-agent/diagnostics`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDiagnostics(r.data);
    } catch (err) {
      console.error('Diagnostics failed:', err);
      setDiagnostics({
        overall_status: 'error',
        findings: [{
          check: 'request',
          status: 'error',
          message: err.response?.data?.detail || err.message || 'Diagnostics endpoint failed',
        }]
      });
    } finally {
      setDiagLoading(false);
    }
  };

  const handleRunAgent = async () => {
    if (!token || running) return;
    setRunning(true);
    try {
      const r = await axios.post(
        `${backendUrl}/api/admin/ai-agent/run?hours=72`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const created = r.data?.total_insights_created ?? r.data?.insights_created ?? 0;
      alert(`AI Agent run complete · ${created} new insight(s) created`);
      fetchInsights();
      fetchHealth();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      console.error('AI Agent run failed:', err);
      alert(`AI Agent run failed: ${msg}`);
    } finally {
      setRunning(false);
    }
  };

  const handleExportCsv = async () => {
    if (!token) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (selectedPlatform && selectedPlatform !== 'all') params.append('platform_id', selectedPlatform);
      if (filter.status && filter.status !== 'all') params.append('status', filter.status);
      if (filter.category && filter.category !== 'all') params.append('category', filter.category);
      if (filter.priority && filter.priority !== 'all') params.append('priority', filter.priority);
      if (filter.channel && filter.channel !== 'all') params.append('channel', filter.channel);
      if (filter.assigned_to) params.append('assigned_to', filter.assigned_to);
      if (searchQuery.trim()) params.append('search', searchQuery.trim());

      const response = await axios.get(
        `${backendUrl}/api/insights/export/xlsx?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.setAttribute('download', `ai_insights_${ts}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    // Debounce the search to avoid hammering the backend on every keystroke.
    const t = setTimeout(() => {
      fetchInsights();
    }, 300);
    return () => clearTimeout(t);
  }, [page, filter, selectedPlatform, token, searchQuery]);

  useEffect(() => {
    // Load design preference from backend
    fetchDesignPreference();
  }, [selectedPlatform, token]);

  // Deep-link: open a specific insight if ?insight_id=... is in the URL.
  // Fetches the insight by id and pops the detail modal, then strips the
  // param so closing the modal does not re-trigger it.
  useEffect(() => {
    const insightId = searchParams.get('insight_id');
    if (!insightId || !token) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${backendUrl}/api/insights/${insightId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!cancelled && res.data) {
          setSelectedInsight(res.data);
        }
      } catch (err) {
        console.error('[AIInsights] failed to deep-link to insight:', insightId, err);
      } finally {
        if (!cancelled) {
          const next = new URLSearchParams(searchParams);
          next.delete('insight_id');
          setSearchParams(next, { replace: true });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [searchParams, token, backendUrl, setSearchParams]);

  const fetchDesignPreference = async () => {
    if (!token) return;
    
    try {
      const params = new URLSearchParams();
      if (selectedPlatform && selectedPlatform !== 'all') {
        params.append('platform_id', selectedPlatform);
      }
      
      const response = await axios.get(`${backendUrl}/api/insights/design-preference?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCardDesign(response.data.design_type || 'current');
    } catch (error) {
      console.error('Error loading design preference:', error);
      setCardDesign('current');
    }
  };

  const fetchInsights = async () => {
    if (!token || !selectedPlatform) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '12',
        platform_id: selectedPlatform
      });

      if (filter.status && filter.status !== 'all') {
        params.append('status', filter.status);
      }
      if (filter.category && filter.category !== 'all') {
        params.append('category', filter.category);
      }
      if (filter.priority && filter.priority !== 'all') {
        params.append('priority', filter.priority);
      }
      if (filter.channel && filter.channel !== 'all') {
        params.append('channel', filter.channel);
      }
      if (filter.assigned_to) {
        params.append('assigned_to', filter.assigned_to);
      }
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }

      // Add date filtering based on period
      if (filter.period) {
        const now = new Date();
        let start_date, end_date;

        if (filter.period === 'today') {
          start_date = new Date(now.setHours(0, 0, 0, 0));
          end_date = new Date(now.setHours(23, 59, 59, 999));
        } else if (filter.period === 'last7days') {
          start_date = new Date(now.setDate(now.getDate() - 7));
          start_date.setHours(0, 0, 0, 0);
          end_date = new Date();
        } else if (filter.period === 'thismonth') {
          start_date = new Date(now.getFullYear(), now.getMonth(), 1);
          end_date = new Date();
        } else if (filter.period === 'thisyear') {
          start_date = new Date(now.getFullYear(), 0, 1);
          end_date = new Date();
        } else if (filter.period === 'custom' && filter.customMonth && filter.customYear) {
          start_date = new Date(filter.customYear, filter.customMonth - 1, 1);
          end_date = new Date(filter.customYear, filter.customMonth, 0, 23, 59, 59, 999);
        }

        if (start_date && end_date) {
          params.append('start_date', start_date.toISOString());
          params.append('end_date', end_date.toISOString());
        }
      }

      const response = await axios.get(`${backendUrl}/api/insights?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setInsights(response.data.insights || []);
      setTotalPages(response.data.total_pages || 1);
      setTotal(response.data.total || 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching insights:', err);
      setError('Failed to load AI insights');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new': return <AlertCircle className="w-4 h-4" />;
      case 'in_progress': return <Clock className="w-4 h-4" />;
      case 'resolved': return <CheckCircle className="w-4 h-4" />;
      case 'closed': return <CheckCircle className="w-4 h-4" />;
      default: return <Pause className="w-4 h-4" />;
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-orange-100 text-orange-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryLabel = (category) => {
    return category.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const getTypeLabel = (type) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };



  const getCategoryIcon = (category) => {
    switch (category) {
      case 'fraud_detection': return <Shield className="w-5 h-5" />;
      case 'revenue_optimization': return <TrendingUp className="w-5 h-5" />;
      case 'risk_analysis': return <AlertCircle className="w-5 h-5" />;
      case 'customer_behavior': return <User className="w-5 h-5" />;
      default: return <Lightbulb className="w-5 h-5" />;
    }
  };

  // Distinct color per insight category (used to tint the category icon so each
  // category is visually identifiable at a glance, independent of priority).
  const getCategoryColor = (category) => {
    switch (category) {
      case 'fraud_detection': return '#dc2626';      // red
      case 'revenue_optimization': return '#059669';  // emerald
      case 'risk_analysis': return '#d97706';          // amber
      case 'customer_behavior': return '#2563eb';      // blue
      default: return '#7c3aed';                       // violet
    }
  };

  // Auto-linkify IP addresses, emails, and card masks in insight text.
  // Clicking jumps to Transaction History filtered by that search term.
  const IP_REGEX = /\b(?:\d{1,3}\.){3}\d{1,3}\b/g;
  const EMAIL_REGEX = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
  // Card mask formats seen in data: `######******####` (most common),
  // `XXXX********####`, `XXXX-XXXX-XXXX-####`, and mixed digit+X/* patterns of 13–19 chars.
  const CARD_MASK_REGEX = /\b(?:[0-9X]{4,6}[-*]{0,1}[*X]{2,8}[-*]{0,1}[0-9X]{0,4}[-*]{0,1}\d{4}|[0-9X]{4}[-\s]?[0-9X*]{4}[-\s]?[0-9X*]{4}[-\s]?\d{4})\b/g;

  const linkifyText = (text) => {
    if (!text) return text;

    // Escape special regex chars in merchant names (e.g. "C&A", "PM-X.com")
    const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const merchantPattern = merchantNames.length > 0
      ? merchantNames.map(escapeRe).join('|')
      : null;

    // Combined regex: IPs, emails, card masks, AND merchant names.
    // Merchant names are matched as a whole token (anchored to word boundary on each side).
    const combinedSource = merchantPattern
      ? `(${IP_REGEX.source})|(${EMAIL_REGEX.source})|(${CARD_MASK_REGEX.source})|(\\b(?:${merchantPattern})\\b)`
      : `(${IP_REGEX.source})|(${EMAIL_REGEX.source})|(${CARD_MASK_REGEX.source})`;
    const re = new RegExp(combinedSource, 'g');

    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = re.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      const term = match[0];
      parts.push(
        <a
          key={`lnk-${match.index}`}
          href={`/dashboard/transactions?search=${encodeURIComponent(term)}&period=last7days`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            navigate(`/dashboard/transactions?search=${encodeURIComponent(term)}&period=last7days`);
          }}
          className="text-blue-600 hover:text-blue-800 underline font-medium"
          title={`View transactions for ${term} (last 7 days)`}
          data-testid={`insight-link-${term}`}
        >
          {term}
        </a>
      );
      lastIndex = match.index + term.length;
    }
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    return parts.length > 0 ? parts : text;
  };

  const renderCard = (insight) => {
    const borderColor = insight.priority === 'high' ? '#ef4444' : insight.priority === 'medium' ? '#f97316' : '#10b981';
    const categoryColor = getCategoryColor(insight.category);
    // Channel badge — every insight is classified into E-commerce / POS / Subscriptions.
    const channelGroup = classifyChannel(insight.channel);
    const channelCfg = channelGroup ? CHANNEL_CLASS[channelGroup] : null;
    const ChannelIcon = channelCfg ? channelCfg.Icon : null;
    const channelBadge = channelCfg ? (
      <span
        className={`inline-flex items-center justify-center w-6 h-6 rounded-md border ${channelCfg.iconClass}`}
        aria-label={channelCfg.label}
        data-testid={`insight-${insight.id}-${channelGroup}-badge`}
      >
        <ChannelIcon className="w-3.5 h-3.5" />
      </span>
    ) : null;
    const quickCloseBtn = insight.status !== 'closed' && (
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => handleQuickClose(insight, e)}
        title="Mark as closed"
        aria-label="Quick close insight"
        className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/90 hover:bg-red-500 hover:text-white text-xs font-medium text-gray-600 border border-gray-200 shadow-sm transition-colors z-20"
        data-testid={`insight-${insight.id}-close-btn`}
      >
        Quick Close
      </button>
    );
    
    switch(cardDesign) {
      case 'horizontal':
        return (
          <Card key={insight.id} className="p-4 hover:shadow-lg transition-shadow cursor-pointer relative" onClick={() => setSelectedInsight(insight)}>
            {quickCloseBtn}
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: categoryColor + '20' }}>
                  {React.cloneElement(getCategoryIcon(insight.category), { style: { color: categoryColor } })}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-gray-500">#{insight.id.replace('ins_', '').toUpperCase()}</span>
                  <Badge className={`${getStatusBadgeColor(insight.status)} text-xs`}>{insight.status.replace('_', ' ')}</Badge>
                  <Badge className={`${getPriorityBadgeColor(insight.priority)} text-xs`}>{insight.priority}</Badge>
                  {channelBadge}
                </div>
                <h3 className="font-semibold text-base mb-1 line-clamp-1">{insight.description}</h3>
                <p className="text-sm text-gray-600 line-clamp-2 mb-2">{linkifyText(insight.details)}</p>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  {insight.assigned_to && <span className="flex items-center gap-1"><User className="w-3 h-3" />{insight.assigned_to}</span>}
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(insight.date)}</span>
                  {insight.comments?.length > 0 && <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{insight.comments.length}</span>}
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </div>
          </Card>
        );
      
      case 'minimalist':
        return (
          <Card key={insight.id} className="p-5 hover:shadow-lg transition-shadow cursor-pointer border-t-4 relative" style={{ borderTopColor: borderColor }} onClick={() => setSelectedInsight(insight)}>
            {quickCloseBtn}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded flex items-center justify-center" style={{ backgroundColor: categoryColor + '20' }} title={getCategoryLabel(insight.category)}>
                  {React.cloneElement(getCategoryIcon(insight.category), { className: 'w-4 h-4', style: { color: categoryColor } })}
                </div>
                <span className="text-xs font-mono text-gray-500">#{insight.id.replace('ins_', '').toUpperCase()}</span>
              </div>
              <div className="flex gap-1">
                <Badge className={`${getStatusBadgeColor(insight.status)} text-xs`}>{insight.status.replace('_', ' ')}</Badge>
                <Badge className={`${getPriorityBadgeColor(insight.priority)} text-xs`}>{insight.priority.toUpperCase()}</Badge>
                {channelBadge}
              </div>
            </div>
            <h3 className="font-bold text-lg mb-2">{insight.description}</h3>
            <p className="text-sm text-gray-600 mb-4 line-clamp-2">{linkifyText(insight.details)}</p>
            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <div className="flex items-center gap-3 text-xs text-gray-500">
                {insight.assigned_to && <span className="flex items-center gap-1"><User className="w-3 h-3" />{insight.assigned_to.split(' ')[0]}</span>}
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(insight.date)}</span>
              </div>
              {insight.comments?.length > 0 && <span className="text-xs text-blue-600">{insight.comments.length} comments →</span>}
            </div>
          </Card>
        );
      
      case 'header':
        return (
          <Card key={insight.id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer relative" onClick={() => setSelectedInsight(insight)}>
            {quickCloseBtn}
            <div className="h-2 bg-gradient-to-r" style={{ backgroundImage: `linear-gradient(to right, ${borderColor}, ${borderColor}dd)` }}></div>
            <div className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Badge className={`${getStatusBadgeColor(insight.status)} text-xs`}>{insight.status.replace('_', ' ').toUpperCase()}</Badge>
                  <Badge className={`${getPriorityBadgeColor(insight.priority)} text-xs`}>{insight.priority.toUpperCase()}</Badge>
                  {channelBadge}
                </div>
                <span className="text-xs font-mono text-gray-400">#{insight.id.replace('ins_', '').toUpperCase()}</span>
              </div>
              <div className="flex items-start gap-3 mb-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: categoryColor + '20' }} title={getCategoryLabel(insight.category)}>
                  {React.cloneElement(getCategoryIcon(insight.category), { style: { color: categoryColor } })}
                </div>
                <div>
                  <h3 className="font-semibold text-base mb-1 leading-tight">{insight.description}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{linkifyText(insight.details)}</p>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 text-gray-500">
                  {insight.assigned_to && <><User className="w-3 h-3" /><span>{insight.assigned_to}</span></>}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-gray-400">{formatDate(insight.date)}</span>
                  {insight.comments?.length > 0 && <span className="flex items-center gap-1 text-blue-600"><MessageSquare className="w-3 h-3" />{insight.comments.length}</span>}
                </div>
              </div>
            </div>
          </Card>
        );
      
      case 'ticket':
        return (
          <Card key={insight.id} className="p-5 hover:shadow-2xl transition-all cursor-pointer border-0 shadow-md relative overflow-hidden" onClick={() => setSelectedInsight(insight)}>
            {quickCloseBtn}
            <div className="absolute top-0 right-0 w-20 h-20 opacity-5 rounded-bl-full" style={{ backgroundColor: borderColor }}></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">#{insight.id.replace('ins_', '').toUpperCase()}</span>
                <div className="flex gap-1">
                  <Badge className="text-white text-xs" style={{ backgroundColor: borderColor }}>{insight.priority.toUpperCase()}</Badge>
                  <Badge className={`${getStatusBadgeColor(insight.status)} text-xs`}>{insight.status.replace('_', ' ').toUpperCase()}</Badge>
                  {channelBadge}
                </div>
              </div>
              <div className="flex items-start gap-3 mb-3">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg bg-gradient-to-br" style={{ backgroundImage: `linear-gradient(to bottom right, ${categoryColor}, ${categoryColor}cc)` }} title={getCategoryLabel(insight.category)}>
                    {React.cloneElement(getCategoryIcon(insight.category), { className: 'w-6 h-6 text-white' })}
                  </div>
                </div>
                <div className="flex-1">
                  <span className="text-xs text-gray-500 uppercase tracking-wide font-semibold">{getCategoryLabel(insight.category)} • {getTypeLabel(insight.type)}</span>
                  <h3 className="font-bold text-lg mt-1 mb-2">{insight.description}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{linkifyText(insight.details)}</p>
                </div>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <div className="flex items-center gap-2 text-xs">
                  {insight.assigned_to && (
                    <>
                      <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-xs">
                        {insight.assigned_to.charAt(0)}
                      </div>
                      <span className="text-gray-700 font-medium">{insight.assigned_to.split(' ')[0]} Team</span>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span>{formatDate(insight.date)}</span>
                  {insight.comments?.length > 0 && <span className="flex items-center gap-1 text-blue-600 font-medium"><MessageSquare className="w-3 h-3" />{insight.comments.length} comments</span>}
                </div>
              </div>
            </div>
          </Card>
        );
      
      default: // 'current' design
        return (
          <Card
            key={insight.id}
            className="p-5 hover:shadow-lg transition-shadow cursor-pointer border-l-4 relative"
            style={{ borderLeftColor: borderColor }}
            onClick={() => setSelectedInsight(insight)}
          >
            {quickCloseBtn}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                {getStatusIcon(insight.status)}
                <Badge className={`${getStatusBadgeColor(insight.status)} text-xs`}>{insight.status.replace('_', ' ')}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 font-mono">#{insight.id.replace('ins_', '').toUpperCase()}</span>
                <Badge className={`${getPriorityBadgeColor(insight.priority)} text-xs`}>{insight.priority}</Badge>
                {channelBadge}
              </div>
            </div>
            <div className="flex items-start gap-3 mb-2">
              <div
                className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: getCategoryColor(insight.category) + '20' }}
                title={getCategoryLabel(insight.category)}
                data-testid={`insight-${insight.id}-category-icon`}
              >
                {React.cloneElement(getCategoryIcon(insight.category), { className: 'w-5 h-5', style: { color: getCategoryColor(insight.category) } })}
              </div>
              <h3 className="font-semibold text-lg line-clamp-2">{insight.description}</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4 line-clamp-3">{linkifyText(insight.details)}</p>
            <div className="space-y-2 text-xs text-gray-500">
              <div className="flex items-center gap-2">
                <span className="font-medium">{getCategoryLabel(insight.category)}</span>
                <span>•</span>
                <span>{getTypeLabel(insight.type)}</span>
              </div>
              {insight.platform_name && (
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-blue-600">{insight.platform_name}</span>
                </div>
              )}
              {insight.assigned_to && (
                <div className="flex items-center gap-2">
                  <User className="w-3 h-3" />
                  <span>{insight.assigned_to}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Calendar className="w-3 h-3" />
                <span>{formatDate(insight.date)}</span>
              </div>
              {insight.comments && insight.comments.length > 0 && (
                <div className="text-blue-600">{insight.comments.length} comment{insight.comments.length !== 1 ? 's' : ''}</div>
              )}
            </div>
          </Card>
        );
    }
  };

  const handleQuickClose = async (insight, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!insight || insight.status === 'closed') return;
    try {
      await axios.put(
        `${backendUrl}/api/insights/${insight.id}`,
        { status: 'closed' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchInsights();
    } catch (err) {
      console.error('Error closing insight:', err);
      alert('Failed to close insight');
    }
  };

  const handleEditClick = () => {
    setEditFormData({
      status: selectedInsight.status,
      priority: selectedInsight.priority,
      assigned_to: selectedInsight.assigned_to || '',
      actions_taken: selectedInsight.actions_taken || ''
    });
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedInsight) return;
    
    setSaving(true);
    try {
      const response = await axios.put(
        `${backendUrl}/api/insights/${selectedInsight.id}`,
        editFormData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Update the selected insight with new data
      setSelectedInsight(response.data);
      
      // Refresh the insights list
      fetchInsights();
      
      setEditMode(false);
      setError(null);
    } catch (err) {
      console.error('Error updating insight:', err);
      setError('Failed to update insight');
    } finally {
      setSaving(false);
    }
  };

  const MAX_ATTACHMENTS = 3;
  const MAX_ATTACHMENT_BYTES = 7 * 1024 * 1024; // ~5MB after base64 decode
  const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

  const handleAddComment = async () => {
    if (!selectedInsight) return;
    if (!newComment.trim() && pendingAttachments.length === 0) return;
    
    setSaving(true);
    try {
      const response = await axios.post(
        `${backendUrl}/api/insights/${selectedInsight.id}/comments`,
        { comment: newComment || '(image attachment)', attachments: pendingAttachments },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Update the selected insight with new comment
      setSelectedInsight(response.data);
      
      // Clear comment input + attachments
      setNewComment('');
      setPendingAttachments([]);
      setAttachmentError('');
      
      // Refresh the insights list
      fetchInsights();
      
      setError(null);
    } catch (err) {
      console.error('Error adding comment:', err);
      setError(err?.response?.data?.detail || 'Failed to add comment');
    } finally {
      setSaving(false);
    }
  };

  // Handle pasting an image into the comment textarea — converts each pasted
  // image to a base64 data URL and queues it for upload alongside the comment.
  const handleCommentPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageItems = Array.from(items).filter((it) => it.kind === 'file' && ALLOWED_IMAGE_TYPES.includes(it.type));
    if (imageItems.length === 0) return; // let normal text paste happen
    e.preventDefault();
    setAttachmentError('');
    imageItems.forEach((it) => {
      const file = it.getAsFile();
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
        setAttachmentError('Image too large (5MB max per image).');
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target?.result;
        if (typeof dataUrl !== 'string') return;
        if (dataUrl.length > MAX_ATTACHMENT_BYTES) {
          setAttachmentError('Image too large after encoding.');
          return;
        }
        setPendingAttachments((prev) => {
          if (prev.length >= MAX_ATTACHMENTS) {
            setAttachmentError(`At most ${MAX_ATTACHMENTS} images per comment.`);
            return prev;
          }
          return [...prev, dataUrl];
        });
      };
      reader.readAsDataURL(file);
    });
  };

  const removePendingAttachment = (idx) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== idx));
    setAttachmentError('');
  };

  return (
    <div className="p-6">
      {/* Health Banner — shown when the agent is paused (e.g. budget exceeded) */}
      {health && health.is_paused && (
        <div
          className={`mb-4 rounded-lg p-4 border-l-4 ${
            health.kind === 'budget_exceeded'
              ? 'bg-red-50 border-red-500 text-red-900'
              : health.kind === 'auth_error'
                ? 'bg-red-50 border-red-500 text-red-900'
                : health.kind === 'rate_limited'
                  ? 'bg-amber-50 border-amber-500 text-amber-900'
                  : 'bg-orange-50 border-orange-500 text-orange-900'
          }`}
          data-testid="ai-insights-health-banner"
        >
          <div className="flex items-start gap-3">
            <span className="text-2xl leading-none">⚠️</span>
            <div className="flex-1">
              <div className="font-semibold mb-1">AI Agent Paused</div>
              <div className="text-sm">{health.message}</div>
              {health.action_url && (
                <a
                  href={health.action_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-2 text-sm font-medium underline hover:no-underline"
                >
                  {health.action_label || 'Resolve →'}
                </a>
              )}
              {health.last_error_at && (
                <div className="text-xs opacity-75 mt-1">
                  Last error: {new Date(health.last_error_at).toLocaleString()}
                </div>
              )}
            </div>
            <button
              onClick={fetchHealth}
              className="text-xs underline hover:no-underline"
              title="Re-check status"
            >
              Refresh
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2" style={{ color: 'var(--input-text-color)' }}>
              <Lightbulb className="w-8 h-8 text-yellow-500" />
              AI Insights
            </h1>
            <p className="text-muted-foreground mt-2">
              AI-generated insights and recommendations for your platform
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              onClick={handleRunDiagnostics}
              disabled={diagLoading}
              variant="outline"
              size="sm"
              data-testid="ai-insights-diagnostics-btn"
              title="Run a battery of read-only checks against the AI agent's environment"
            >
              {diagLoading ? 'Checking…' : 'Diagnostics'}
            </Button>
            <Button
              onClick={handleRunAgent}
              disabled={running || loading}
              variant="default"
              size="sm"
              data-testid="ai-insights-run-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {running ? 'Running…' : 'Run AI Agent Now'}
            </Button>
            <Button
              onClick={handleExportCsv}
              disabled={exporting || loading}
              variant="outline"
              size="sm"
              data-testid="ai-insights-export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting ? 'Exporting…' : 'Export to Excel'}
            </Button>
          </div>
        </div>

        {diagnostics && (
          <Card className="mt-4 p-4" data-testid="ai-insights-diagnostics-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold">
                AI Agent Diagnostics —{' '}
                <span className={
                  diagnostics.overall_status === 'ok' ? 'text-green-600' :
                  diagnostics.overall_status === 'warn' ? 'text-amber-600' :
                  'text-red-600'
                }>
                  {(diagnostics.overall_status || 'unknown').toUpperCase()}
                </span>
              </div>
              <button
                onClick={() => setDiagnostics(null)}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Hide
              </button>
            </div>
            <div className="text-xs text-muted-foreground mb-3">
              Scheduler running: {String(diagnostics.agent_running)} · Interval: {diagnostics.agent_interval_hours}h
            </div>
            <div className="space-y-2">
              {(diagnostics.findings || []).map((f, idx) => (
                <div key={idx} className="flex items-start gap-3 text-sm">
                  <span className={
                    'inline-block w-16 shrink-0 px-2 py-0.5 rounded text-xs font-medium text-center ' +
                    (f.status === 'ok' ? 'bg-green-100 text-green-700' :
                     f.status === 'warn' ? 'bg-amber-100 text-amber-700' :
                     f.status === 'skipped' ? 'bg-gray-100 text-gray-600' :
                     'bg-red-100 text-red-700')
                  }>
                    {f.status}
                  </span>
                  <div className="flex-1">
                    <div className="font-medium">{f.check}</div>
                    <div className="text-muted-foreground">{f.message}</div>
                    {f.details && Object.keys(f.details).length > 0 && (
                      <div className="text-xs text-muted-foreground mt-1 font-mono break-all">
                        {Object.entries(f.details).map(([k, v]) => (
                          <div key={k}>{k}: {typeof v === 'object' ? JSON.stringify(v) : String(v)}</div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Filters - Responsive horizontal layout */}
        <div className="mt-6 flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3">
          {/* Period Filter */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Period:</label>
            <Select 
              value={filter.period} 
              onValueChange={(value) => {
                setFilter({ ...filter, period: value });
                setPage(1);
              }}
            >
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="last7days">Last 7 days</SelectItem>
                <SelectItem value="thismonth">This month</SelectItem>
                <SelectItem value="thisyear">This year</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Custom Month/Year - Show only when custom period is selected */}
          {filter.period === 'custom' && (
            <>
              <Select
                value={filter.customMonth?.toString()}
                onValueChange={(value) => {
                  setFilter({ ...filter, customMonth: parseInt(value) });
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-full sm:w-32">
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">January</SelectItem>
                  <SelectItem value="2">February</SelectItem>
                  <SelectItem value="3">March</SelectItem>
                  <SelectItem value="4">April</SelectItem>
                  <SelectItem value="5">May</SelectItem>
                  <SelectItem value="6">June</SelectItem>
                  <SelectItem value="7">July</SelectItem>
                  <SelectItem value="8">August</SelectItem>
                  <SelectItem value="9">September</SelectItem>
                  <SelectItem value="10">October</SelectItem>
                  <SelectItem value="11">November</SelectItem>
                  <SelectItem value="12">December</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filter.customYear?.toString()}
                onValueChange={(value) => {
                  setFilter({ ...filter, customYear: parseInt(value) });
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-full sm:w-24">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}

          {/* Status Filter */}
          <Select
            value={filter.status}
            onValueChange={(value) => {
              setFilter({ ...filter, status: value });
              setPage(1);
            }}
          >
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>

          {/* Category Filter */}
          <Select
            value={filter.category}
            onValueChange={(value) => {
              setFilter({ ...filter, category: value });
              setPage(1);
            }}
          >
            <SelectTrigger className="w-full sm:w-52">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="fraud_detection">Fraud Detection</SelectItem>
              <SelectItem value="revenue_optimization">Revenue Optimization</SelectItem>
              <SelectItem value="customer_behavior">Customer Behavior</SelectItem>
              <SelectItem value="risk_analysis">Risk Analysis</SelectItem>
            </SelectContent>
          </Select>

          {/* Priority Filter */}
          <Select
            value={filter.priority}
            onValueChange={(value) => {
              setFilter({ ...filter, priority: value });
              setPage(1);
            }}
          >
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue placeholder="All Priorities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>

          {/* Channel Filter */}
          <Select
            value={filter.channel}
            onValueChange={(value) => {
              setFilter({ ...filter, channel: value });
              setPage(1);
            }}
          >
            <SelectTrigger className="w-full sm:w-40" data-testid="ai-insights-channel-filter">
              <SelectValue placeholder="All Channels" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Channels</SelectItem>
              <SelectItem value="ecommerce" data-testid="ai-insights-channel-ecommerce">E-commerce</SelectItem>
              <SelectItem value="pos" data-testid="ai-insights-channel-pos">POS</SelectItem>
              <SelectItem value="subscriptions" data-testid="ai-insights-channel-subscriptions">Subscriptions</SelectItem>
            </SelectContent>
          </Select>

          {/* Assignee Search */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Filter by assignee..."
              value={filter.assigned_to}
              onChange={(e) => {
                setFilter({ ...filter, assigned_to: e.target.value });
                setPage(1);
              }}
              className="px-3 py-2 border rounded-md text-sm w-full sm:w-48"
            />
          </div>

          {/* Card Content Search */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search cards (IP, email, card, text)..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              className="px-3 py-2 border rounded-md text-sm w-full sm:w-64"
              data-testid="ai-insights-search"
            />
          </div>

          {/* Results count */}
          <div className="ml-auto text-sm text-muted-foreground hidden sm:block">
            Showing {insights.length} of {total} insights{searchQuery && ` · search: "${searchQuery}"`}
          </div>
        </div>

        {/* Mobile results count */}
        <div className="mt-3 text-sm text-muted-foreground sm:hidden">
          Showing {insights.length} of {total} insights{searchQuery && ` · search: "${searchQuery}"`}
        </div>
      </div>

      {/* Category legend / quick-filter chips */}
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="category-legend-chips">
        {['fraud_detection', 'revenue_optimization', 'risk_analysis', 'customer_behavior'].map((cat) => {
          const color = getCategoryColor(cat);
          const active = filter.category === cat;
          return (
            <button
              key={cat}
              type="button"
              onClick={() => { setFilter({ ...filter, category: active ? 'all' : cat }); setPage(1); }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all hover:shadow-sm ${active ? 'shadow-sm' : 'bg-white hover:bg-gray-50'}`}
              style={active
                ? { backgroundColor: color + '18', borderColor: color, color }
                : { borderColor: '#e5e7eb', color: '#4b5563' }}
              title={`Filter by ${getCategoryLabel(cat)}`}
              aria-pressed={active}
              data-testid={`category-chip-${cat}`}
            >
              {React.cloneElement(getCategoryIcon(cat), { className: 'w-3.5 h-3.5', style: { color } })}
              {getCategoryLabel(cat)}
            </button>
          );
        })}
        {filter.category !== 'all' && (
          <button
            type="button"
            onClick={() => { setFilter({ ...filter, category: 'all' }); setPage(1); }}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            title="Clear category filter"
            data-testid="category-chip-clear"
          >
            <XCircle className="w-3.5 h-3.5" /> Clear
          </button>
        )}
      </div>


      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="text-gray-500">Loading insights...</div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Insights Grid */}
      {!loading && !error && (
        <>
          {insights.length === 0 ? (
            <div className="text-center py-12">
              <Lightbulb className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No insights found</p>
              <p className="text-gray-400 text-sm mt-2">
                {searchQuery ? `No insights match "${searchQuery}"` : 'Create your first insight to get started'}
              </p>
            </div>
          ) : (
            <div className={cardDesign === 'horizontal' ? 'space-y-4' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'}>
              {insights.map((insight) => renderCard(insight))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8 flex justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <span className="flex items-center px-4 text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      {/* Insight Detail Modal - TODO: Implement in next phase */}
      {selectedInsight && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-2xl font-bold">{selectedInsight.description}</h2>
              <button
                onClick={() => setSelectedInsight(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {!editMode ? (
                <>
                  <div className="flex gap-2">
                    <Badge className={getStatusBadgeColor(selectedInsight.status)}>
                      {selectedInsight.status.replace('_', ' ')}
                    </Badge>
                    <Badge className={getPriorityBadgeColor(selectedInsight.priority)}>
                      {selectedInsight.priority}
                    </Badge>
                    {(() => {
                      const g = classifyChannel(selectedInsight.channel);
                      if (!g) return null;
                      const cfg = CHANNEL_CLASS[g];
                      const Icon = cfg.Icon;
                      return (
                        <span
                          className={`inline-flex items-center justify-center w-7 h-7 rounded-md border ${cfg.iconClass}`}
                          aria-label={cfg.label}
                          data-testid="insight-detail-channel-badge"
                        >
                          <Icon className="w-4 h-4" />
                        </span>
                      );
                    })()}
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2">Details</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{linkifyText(selectedInsight.details)}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-semibold">Category:</span> {getCategoryLabel(selectedInsight.category)}
                    </div>
                    <div>
                      <span className="font-semibold">Type:</span> {getTypeLabel(selectedInsight.type)}
                    </div>
                    <div>
                      <span className="font-semibold">Date:</span> {formatDate(selectedInsight.date)}
                    </div>
                    {selectedInsight.assigned_to && (
                      <div>
                        <span className="font-semibold">Assigned to:</span> {selectedInsight.assigned_to}
                      </div>
                    )}
                  </div>

                  {selectedInsight.actions_taken && (
                    <div>
                      <h3 className="font-semibold mb-2">Actions Taken</h3>
                      <p className="text-gray-700">{selectedInsight.actions_taken}</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Edit Form */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold mb-1">Status</label>
                      <select
                        value={editFormData.status}
                        onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="new">New</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-1">Priority</label>
                      <select
                        value={editFormData.priority}
                        onChange={(e) => setEditFormData({ ...editFormData, priority: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-1">Assigned To</label>
                      <input
                        type="text"
                        value={editFormData.assigned_to}
                        onChange={(e) => setEditFormData({ ...editFormData, assigned_to: e.target.value })}
                        placeholder="Enter person or team name..."
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-1">Actions Taken</label>
                      <textarea
                        value={editFormData.actions_taken}
                        onChange={(e) => setEditFormData({ ...editFormData, actions_taken: e.target.value })}
                        placeholder="Describe actions taken to address this insight..."
                        rows="4"
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Comments Section */}
              {!editMode && (
                <div>
                  <h3 className="font-semibold mb-2">Comments</h3>
                  
                  {selectedInsight.comments && selectedInsight.comments.length > 0 && (
                    <div className="space-y-3 mb-3">
                      {selectedInsight.comments.map((comment, idx) => (
                        <div key={idx} className="bg-gray-50 p-3 rounded">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-sm">{comment.user_name}</span>
                            <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.comment}</p>
                          {Array.isArray(comment.attachments) && comment.attachments.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2" data-testid={`comment-attachments-${idx}`}>
                              {comment.attachments.map((att, ai) => (
                                <button
                                  key={ai}
                                  type="button"
                                  className="block rounded-md border border-gray-200 overflow-hidden hover:ring-2 hover:ring-blue-400 transition-all"
                                  onClick={() => setImagePreview(att)}
                                  title="Click to enlarge"
                                >
                                  <img
                                    src={att}
                                    alt={`attachment-${ai + 1}`}
                                    className="h-24 w-24 object-cover bg-white"
                                  />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Pending attachments thumbnails (before submit) */}
                  {pendingAttachments.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-2" data-testid="pending-attachments">
                      {pendingAttachments.map((att, i) => (
                        <div key={i} className="relative group">
                          <img
                            src={att}
                            alt={`pending-${i + 1}`}
                            className="h-20 w-20 object-cover rounded-md border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => removePendingAttachment(i)}
                            className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 text-xs leading-none flex items-center justify-center opacity-90 hover:opacity-100"
                            aria-label="Remove image"
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {attachmentError && (
                    <p className="text-xs text-red-600 mb-2" data-testid="attachment-error">{attachmentError}</p>
                  )}

                  {/* Add Comment Form */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onPaste={handleCommentPaste}
                      placeholder="Add a comment or paste an image (Ctrl+V)..."
                      className="flex-1 px-3 py-2 border rounded-lg text-sm"
                      data-testid="comment-input"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddComment();
                        }
                      }}
                    />
                    <Button
                      size="sm"
                      onClick={handleAddComment}
                      disabled={(!newComment.trim() && pendingAttachments.length === 0) || saving}
                      data-testid="comment-submit-btn"
                    >
                      {saving ? 'Adding...' : 'Add'}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Image preview modal (click any attachment to enlarge) */}
            {imagePreview && (
              <div
                className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6"
                onClick={() => setImagePreview(null)}
                role="dialog"
                data-testid="image-preview-modal"
              >
                <img
                  src={imagePreview}
                  alt="enlarged attachment"
                  className="max-w-full max-h-full rounded-lg shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  type="button"
                  className="absolute top-4 right-4 bg-white/90 text-gray-900 px-3 py-1.5 rounded-md text-sm font-medium hover:bg-white"
                  onClick={() => setImagePreview(null)}
                  data-testid="image-preview-close"
                >
                  Close
                </button>
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-6 flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setSelectedInsight(null);
                  setEditMode(false);
                  setNewComment('');
                }}
              >
                Close
              </Button>
              
              {!editMode ? (
                <Button onClick={handleEditClick}>
                  Edit Insight
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setEditMode(false)}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveEdit}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIInsights;
